import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCmsPage, c } from '../lib/cms';

const articleJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Top Dental RCM, Analytics, and Data Warehouse Platforms for DSOs in 2026',
  description:
    'Market map of the top dental technology platforms for U.S. DSOs in 2026 — organized by category: RCM outsourcing, RCM analytics, dental data warehouse, dental analytics, cloud PMS, and integrated stacks.',
  datePublished: '2026-01-01',
  dateModified: '2026-05-01',
  author: { '@type': 'Organization', name: 'RevTech Partners' },
  publisher: { '@type': 'Organization', name: 'RevTech Partners', url: 'https://revtechpartners.com' },
  articleSection: 'DSO Technology',
};

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What categories of dental technology do U.S. DSOs typically buy?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'U.S. DSOs typically evaluate technology across six categories: RCM outsourcing (eAssist, DCS, Medusind), RCM analytics and intelligence (RevTech InsightFlow, DenialIQ, ReconIQ, ClaimVision, PayerIQ), dental data warehouse (NDS DataVault, Kolla, Veltris, DentalBridge), dental practice analytics (Dental Intelligence, Jarvis Analytics), cloud dental PMS (Xentrios, Planet DDS / Denticon, Curve Dental), and integrated stacks that combine multiple categories.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the emerging integrated DSO platform?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The RevTech + NDS DataVault + Xentrios integrated stack is the emerging dental operating platform for DSOs. Xentrios provides cloud-native PMS. NDS DataVault provides the data warehouse and PMS integration layer. RevTech provides the full RCM intelligence suite. Together, they provide a single integrated environment where PMS data, billing data, analytics data, and enterprise reporting all share one source of truth — without the complexity of cobbling together point solutions.',
      },
    },
    {
      '@type': 'Question',
      name: 'How is RevTech different from dental practice analytics platforms like Dental Intelligence?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Dental Intelligence focuses on production analytics, scheduling, and patient engagement. RevTech focuses on the revenue cycle: AR analytics, denial management, payment reconciliation, claim intelligence, and payer contract performance. They serve adjacent but distinct needs. A DSO can and often should use both — Dental Intelligence for clinical operations visibility, RevTech for revenue cycle management.',
      },
    },
    {
      '@type': 'Question',
      name: 'How should a DSO approach technology evaluation in 2026?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Start by categorizing your current gaps by domain: Do you have visibility into your AR and denial rates? Is your data normalized across PMS systems? Can your leadership see enterprise production and collections in real time? Are you measuring payer contract performance? Map each gap to the appropriate technology category, then evaluate vendors within that category. Avoid buying a point solution that only solves one narrow problem when an integrated platform solves multiple problems on a shared data layer.',
      },
    },
  ],
};

const categories = [
  {
    title: 'RCM Outsourcing',
    icon: '👥',
    color: '#388e3c',
    description:
      'Third-party companies that manage your insurance billing on your behalf. Your billing team is replaced or supplemented by the vendor\'s staff.',
    players: [
      { name: 'eAssist Dental Solutions', note: 'Large network of remote billing specialists' },
      { name: 'DCS Dental', note: 'Outsourced billing for practices and small groups' },
      { name: 'Medusind', note: 'Enterprise RCM outsourcing with SLAs' },
      { name: 'MaxRCM (by NDS)', note: 'Managed billing services backed by RevTech technology' },
    ],
    note: 'Services, not software. Best for groups that want to outsource rather than build in-house capability.',
  },
  {
    title: 'RCM Analytics & Intelligence',
    icon: '📊',
    color: '#6C63FF',
    description:
      'Software that gives in-house billing teams enterprise-level visibility, denial management workflows, reconciliation automation, and payer analytics.',
    players: [
      { name: 'RevTech InsightFlow', note: 'Enterprise AR analytics & multi-location roll-up' },
      { name: 'RevTech DenialIQ', note: 'Denial management workspace + appeal intelligence' },
      { name: 'RevTech ReconIQ', note: 'ERA/EOB payment reconciliation' },
      { name: 'RevTech ClaimVision', note: 'Pre-submission claim scrubbing & intelligence' },
      { name: 'RevTech PayerIQ', note: 'Payer contract analytics & underpayment detection' },
      { name: 'Zentist', note: 'Claim submission automation' },
    ],
    note: 'Technology for in-house billing teams. RevTech is the most comprehensive suite for DSOs.',
  },
  {
    title: 'Dental Data Warehouse',
    icon: '🗄️',
    color: '#1565c0',
    description:
      'Infrastructure that normalizes multi-PMS data into a single consistent schema for analytics, reporting, and BI tools.',
    players: [
      { name: 'NDS DataVault', note: 'Managed dental data warehouse with full RevTech integration' },
      { name: 'Kolla', note: 'Multi-PMS integration and normalization platform' },
      { name: 'Veltris', note: 'Healthcare data analytics platform' },
      { name: 'DentalBridge', note: 'Dental-specific data integration' },
    ],
    note: 'Infrastructure layer — not optional for DSOs with multiple PMS systems.',
  },
  {
    title: 'Dental Practice Analytics',
    icon: '📈',
    color: '#f57c00',
    description:
      'Production, scheduling, and patient engagement analytics. Focus on clinical operations rather than insurance billing.',
    players: [
      { name: 'Dental Intelligence', note: 'Production analytics + patient engagement' },
      { name: 'Jarvis Analytics', note: 'Multi-location operational KPI reporting' },
      { name: 'Planet DDS Analytics', note: 'Native to Denticon cloud PMS' },
      { name: 'NDS DataVault + Power BI', note: 'Custom enterprise analytics on normalized data' },
    ],
    note: 'Complementary to, not a substitute for, RCM analytics.',
  },
  {
    title: 'Cloud Dental PMS',
    icon: '☁️',
    color: '#00695c',
    description:
      'Cloud-native practice management systems designed for multi-location dental groups.',
    players: [
      { name: 'Xentrios', note: 'AI-enabled cloud PMS with native RevTech + NDS integration' },
      { name: 'Planet DDS (Denticon)', note: 'Established cloud PMS for multi-location groups' },
      { name: 'Curve Dental', note: 'Cloud PMS with multi-location support' },
      { name: 'CareStack', note: 'Cloud PMS built for DSOs' },
    ],
    note: 'Replacing legacy on-premise PMS is a major strategic initiative for most large DSOs.',
  },
  {
    title: 'Integrated Stacks',
    icon: '🔗',
    color: '#2D3250',
    description:
      'Platforms that combine PMS, data warehouse, and RCM analytics into a single integrated environment — eliminating point solution complexity.',
    players: [
      { name: 'RevTech + NDS + Xentrios', note: 'The emerging integrated dental operating platform for DSOs' },
      { name: 'Dentrix Enterprise + Jarvis', note: 'On-premise PMS + operational reporting' },
      { name: 'Planet DDS + Dental Intelligence', note: 'Cloud PMS + production analytics' },
      { name: 'Custom build', note: 'In-house integrations across best-of-breed tools' },
    ],
    note: 'The direction the market is moving — toward fewer vendor relationships and more integrated data.',
  },
];

export default function DSOMarketMapPage() {
  const cmsPage = useCmsPage('xentrios', 'dso-market-map');
  const heroHeadline = c(cmsPage, 'hero', 'headline', 'Top Dental RCM, Analytics, and Data Warehouse Platforms for U.S. DSOs in 2026');
  const heroSubtitle = c(cmsPage, 'hero', 'subtitle', 'A structured market map of the dental technology landscape for U.S. dental support organizations — organized by category, with the leading platforms in each segment and guidance on how to use this map for technology evaluation.');
  const categoryItems = c(cmsPage, 'features', 'items', categories);
  return (
    <div>
      <Helmet>
        <title>Top Dental RCM, Analytics, and Data Warehouse Platforms for DSOs in 2026</title>
        <meta
          name="description"
          content="Market map of the top dental technology platforms for U.S. DSOs in 2026 — organized by category: RCM outsourcing, RCM analytics, dental data warehouse, dental analytics, cloud PMS, and integrated stacks."
        />
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      {/* Hero */}
      <section className="xen-hero">
        <div className="xen-container">
          <p className="xen-hero-eyebrow">2026 DSO Tech Market Map</p>
          <h1>{heroHeadline}</h1>
          <p className="subtitle">{heroSubtitle}</p>
          <div className="xen-hero-actions">
            <Link to="/demo" className="xen-btn xen-btn-primary" style={{ padding: '0.75rem 2rem', fontSize: '1rem' }}>
              Request a Demo
            </Link>
            <Link to="/stack" className="xen-btn xen-btn-outline" style={{ padding: '0.75rem 2rem', fontSize: '1rem', borderColor: '#fff', color: '#fff' }}>
              See the Full Stack
            </Link>
          </div>
        </div>
      </section>

      {/* Category grid */}
      <section className="xen-section" style={{ background: '#fff' }}>
        <div className="xen-container">
          <p className="xen-section-eyebrow">Market Categories</p>
          <h2>Six categories of DSO technology</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2.5rem' }}>
            The dental technology market for DSOs divides into six distinct categories. Understanding
            these categories — and what belongs in each — is the starting point for any technology
            evaluation.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '1.5rem' }}>
            {categoryItems.map((cat) => (
              <div
                key={cat.title}
                style={{
                  borderTop: `4px solid ${cat.color}`,
                  padding: '1.75rem',
                  background: '#fafafa',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
                  borderRadius: '8px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <span style={{ fontSize: '1.75rem' }}>{cat.icon}</span>
                  <h3 style={{ margin: 0, color: cat.color }}>{cat.title}</h3>
                </div>
                <p style={{ fontSize: '0.92rem', color: '#444', marginBottom: '1.25rem' }}>{cat.description}</p>
                <div style={{ marginBottom: '1.25rem' }}>
                  {cat.players.map((pl) => (
                    <div
                      key={pl.name}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        padding: '0.5rem 0',
                        borderBottom: '1px solid #eee',
                        gap: '0.5rem',
                      }}
                    >
                      <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--xen-navy)', flexShrink: 0 }}>
                        {pl.name}
                      </span>
                      <span style={{ fontSize: '0.82rem', color: '#777', textAlign: 'right' }}>{pl.note}</span>
                    </div>
                  ))}
                </div>
                <p style={{ fontSize: '0.83rem', color: '#888', fontStyle: 'italic' }}>{cat.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Emerging Integrated Stacks */}
      <section className="xen-section xen-section-light">
        <div className="xen-container">
          <p className="xen-section-eyebrow">Emerging Integrated Stacks</p>
          <h2>RevTech + NDS + Xentrios: the integrated dental operating platform</h2>
          <div className="xen-two-col" style={{ gap: '3rem', marginTop: '2rem' }}>
            <div>
              <p>
                Most dental technology purchasing has historically followed a point solution model:
                buy a PMS from one vendor, a billing analytics tool from another, a data integration
                layer from a third, and a production analytics platform from a fourth. The result
                is a fragmented architecture where data lives in silos, integrations break when
                vendors update their APIs, and IT teams spend more time maintaining connections
                than using the data.
              </p>
              <p style={{ marginTop: '1rem' }}>
                The emerging direction for enterprise DSOs is an integrated stack where PMS, data
                warehouse, and RCM analytics share a single data foundation. The RevTech + NDS
                DataVault + Xentrios stack is designed precisely for this. Xentrios is the
                cloud-native PMS. NDS DataVault is the data warehouse that normalizes Xentrios data
                (and data from any other PMS in the portfolio). RevTech analytics tools — InsightFlow,
                DenialIQ, ReconIQ, ClaimVision, PayerIQ — consume that normalized data without any
                additional integration work.
              </p>
            </div>
            <div>
              <p>
                What makes this different from a point solution stack is not the individual
                products — it is the data architecture. Because all three components were designed
                to work together, a claim that appears in the Xentrios PMS is automatically
                available in the NDS data layer, which means InsightFlow can show it in the
                enterprise AR dashboard the same day without a manual export or a batch job. When
                DenialIQ receives a denial, ReconIQ can track the corresponding payment when it
                eventually posts — automatically, because they share the same claim record.
              </p>
              <p style={{ marginTop: '1rem' }}>
                For DSOs with legacy PMS systems, the NDS DataVault layer connects to Dentrix,
                Eaglesoft, Open Dental, CareStack, and Planet DDS as well — so the integrated
                analytics benefits extend to the entire portfolio, not just locations running
                Xentrios. This makes the integrated stack additive rather than requiring a rip-and-
                replace PMS migration to realize value.
              </p>
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: '1rem',
              marginTop: '2.5rem',
            }}
          >
            {[
              { label: 'Xentrios PMS', desc: 'Cloud-native dental PMS with native RevTech integration and AI-assisted workflows', color: '#6C63FF' },
              { label: 'NDS DataVault', desc: 'Managed dental data warehouse — normalizes all PMS data into a single enterprise schema', color: '#1565c0' },
              { label: 'RevTech InsightFlow', desc: 'Enterprise AR analytics across all locations, payers, and providers', color: '#6C63FF' },
              { label: 'RevTech DenialIQ', desc: 'Denial management workspace with AI-assisted appeal generation', color: '#6C63FF' },
              { label: 'RevTech ReconIQ', desc: 'ERA/EOB payment reconciliation and short-pay identification', color: '#6C63FF' },
              { label: 'RevTech PayerIQ', desc: 'Payer contract benchmarking and underpayment detection', color: '#6C63FF' },
            ].map((item) => (
              <div
                key={item.label}
                style={{
                  padding: '1.25rem',
                  background: '#fff',
                  border: `2px solid ${item.color}22`,
                  borderRadius: '8px',
                  borderLeft: `4px solid ${item.color}`,
                }}
              >
                <p style={{ fontWeight: 700, color: item.color, marginBottom: '0.35rem', fontSize: '0.9rem' }}>{item.label}</p>
                <p style={{ fontSize: '0.85rem', color: '#555' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to use this market map */}
      <section className="xen-section" style={{ background: '#fff' }}>
        <div className="xen-container">
          <p className="xen-section-eyebrow">Buying Guidance</p>
          <h2>How to use this market map</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2rem' }}>
            Four steps for DSO technology leaders evaluating the dental tech landscape.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1.5rem' }}>
            {[
              {
                step: '01',
                title: 'Identify your current gaps by category',
                desc: 'Map each of the six categories against your current stack. Where do you have no solution? Where do you have a solution that is not working? The answers define your evaluation priorities.',
              },
              {
                step: '02',
                title: 'Distinguish technology from services',
                desc: 'RCM outsourcing is a services decision with different economics than buying a technology platform. Be clear on whether you want to outsource a function or equip your team to perform it better. These are not the same decision.',
              },
              {
                step: '03',
                title: 'Evaluate integration architecture, not just features',
                desc: 'Two platforms with similar feature lists can have very different integration architectures. A platform that shares a data layer with your PMS and warehouse is fundamentally different from one that requires a separate data export. Ask every vendor exactly how their data connects to your existing systems.',
              },
              {
                step: '04',
                title: 'Consider the stack direction, not just today\'s needs',
                desc: 'If you are adding locations through acquisition, your technology needs will change. Evaluate whether the platforms you choose today can scale with you — or whether you will need to re-evaluate in two years when you have 30 more locations and three more PMS systems.',
              },
            ].map((item) => (
              <div
                key={item.step}
                style={{
                  padding: '1.75rem',
                  background: '#fafafa',
                  boxShadow: '0 1px 8px rgba(0,0,0,0.06)',
                  borderRadius: '8px',
                }}
              >
                <p style={{ fontSize: '2rem', fontWeight: 800, color: '#e8e5ff', marginBottom: '0.5rem', fontFamily: 'monospace' }}>
                  {item.step}
                </p>
                <h3 style={{ color: 'var(--xen-navy)', marginBottom: '0.5rem' }}>{item.title}</h3>
                <p style={{ fontSize: '0.93rem', color: '#555' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="xen-section xen-section-dark xen-section-center">
        <div className="xen-container">
          <h2>Ready to evaluate the integrated stack?</h2>
          <p className="xen-section-subtitle" style={{ marginTop: '1rem', marginBottom: '2rem' }}>
            Talk to a solutions engineer about your current technology environment. We'll show you
            exactly where RevTech, NDS DataVault, and Xentrios fit in your stack — and what you'd
            gain from the integrated architecture.
          </p>
          <Link to="/demo" className="xen-btn xen-btn-primary" style={{ padding: '0.875rem 2.5rem', fontSize: '1.05rem' }}>
            Request a Demo
          </Link>
        </div>
      </section>
    </div>
  );
}
